import { useState, useEffect } from "react";
import { rtdb } from "../../services/firebase";
import { ChatUser } from "../../types/db/chat";
import { WithID } from "../../types/db";
import { ChatUserService } from "../../types/services/chat";

/** User info is the info of a user within the chat feature */
type UserInfo = WithID<ChatUser>;

/**
 * Hook which takes care of fetching and updating the user info of a chat
 * @param userID The user ID
 * @returns the user info
 */
export const useChatUser = (userId: string): {
    /** The user info */
    user: UserInfo | null;
    /** If any action is currently loading */
    isLoading?: boolean;
    /** If any action ended up with an error */
    error?: string;
} => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [user, setUser] = useState<UserInfo | null>(null);

    useEffect(() => {
        if (!userId) {
            //if no userId no need to go farther
            setIsLoading(false);
            return;
        }
        //update the current user with the user info from BE
        setIsLoading(true);
        //subscribe to any child/info of that user
        const fetchUserAbout = (new ChatUserService(rtdb)).getEntityRef(userId);

        fetchUserAbout
            .on("value", (snapshot) => {
                const userAbout: ChatUser | null = snapshot.val();
                if (!snapshot.exists() || !userAbout) {
                    //can happen if doesn't exist
                    setError('User not found');
                    setUser(null);
                    return;
                }
                setUser({ ...userAbout, id: userId });
                setIsLoading(false);
            });

        return () => {
            //unsubscribe to all listener for the user
            fetchUserAbout.off();
            //reset the user info
            setUser(null);
        };
    }, [userId]);

    useEffect(() => {
        //from the nature of the subscription, it is possible that the user doesn't exist
        //so `setIsLoading(false)` never called, therefore we add a timeout
        setTimeout(() => setIsLoading(false), 1000)
    }, []);

    return {
        user,
        isLoading,
        error,
    }
}